import React, { useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import Hls from 'hls.js';
import { Globe, Cpu, Workflow, Code, Zap, Rocket, Building2, Megaphone } from 'lucide-react';
import { LiquidMetalButton } from '../components/ui/liquid-metal-button';
import { BentoGrid } from '../components/ui/bento-grid';
import { Sparkles } from '../components/ui/sparkles';
import { InfiniteSlider } from '../components/ui/infinite-slider';
import { OpenAI, Anthropic, Google, MistralAI, DeepSeek } from '../components/ui/ai-icons';
import AIBentoGrid from '../components/AIBentoGrid';
import TeamSelector from '../components/ui/TeamSelector';
import BackgroundVideo from '../assets/Background.mp4';
import ServiceBackgroundVideo from '../assets/Background_for_service_compressed.mp4';

/* ── Shared animation config ─────────────────────────────────────────── */
const easeIn  = [0.25, 0.1, 0.25, 1];
const easeOut = [0.25, 1, 0.5, 1];

// Trigger slightly inside the viewport to allow smooth fading before clipping
const vp = { once: false, margin: "-15% 0px -15% 0px" };

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: easeIn } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeOut } }
};
const fadeLeft = {
  hidden: { opacity: 0, x: -30, transition: { duration: 0.5, ease: easeIn } },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: easeOut } }
};
const fadeRight = {
  hidden: { opacity: 0, x: 30, transition: { duration: 0.5, ease: easeIn } },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: easeOut } }
};
const fadeRightDelayed = {
  hidden: { opacity: 0, x: 30, transition: { duration: 0.5, ease: easeIn } },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: easeOut, delay: 0.1 } }
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.95, transition: { duration: 0.5, ease: easeIn } },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: easeOut } }
};

/* ── Partner Logo SVGs ───────────────────────────────────────────────── */
const LogoVercel = ({ className }) => (
  <svg viewBox="0 0 76 65" fill="currentColor" className={className}>
    <path d="M37.5274 0L75.0548 65H0L37.5274 0z" />
  </svg>
);
const LogoReact = ({ className }) => (
  <svg viewBox="-11.5 -10.232 23 20.463" fill="none" className={className}>
    <circle r="2.05" fill="currentColor" />
    <g stroke="currentColor" strokeWidth="1">
      <ellipse rx="11" ry="4.2" />
      <ellipse rx="11" ry="4.2" transform="rotate(60)" />
      <ellipse rx="11" ry="4.2" transform="rotate(120)" />
    </g>
  </svg>
);
const LogoN8N = ({ className }) => (
  <svg viewBox="0 0 60 60" fill="none" className={className}>
    <rect width="60" height="60" rx="12" fill="#EA4B71"/>
    <path d="M13 30c0-9.389 7.611-17 17-17s17 7.611 17 17-7.611 17-17 17" stroke="white" strokeWidth="5" strokeLinecap="round"/>
    <circle cx="30" cy="13" r="4" fill="white"/>
    <circle cx="30" cy="47" r="4" fill="white"/>
  </svg>
);
const LogoMake = ({ className }) => (
  <svg viewBox="0 0 60 60" fill="none" className={className}>
    <rect width="60" height="60" rx="12" fill="#6D00CC"/>
    <path d="M12 38 L22 22 L30 34 L38 22 L48 38" stroke="white" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const logos = [
  { id: 'openai',    Icon: OpenAI,    label: 'OpenAI'    },
  { id: 'anthropic', Icon: Anthropic,  label: 'Anthropic' },
  { id: 'google',    Icon: Google,     label: 'Gemini'    },
  { id: 'mistral',   Icon: MistralAI,  label: 'Mistral'   },
  { id: 'deepseek',  Icon: DeepSeek,   label: 'DeepSeek'  },
  { id: 'vercel',    Icon: LogoVercel, label: 'Vercel'    },
  { id: 'react',     Icon: LogoReact,  label: 'React'     },
  { id: 'n8n',       Icon: LogoN8N,    label: 'n8n'       },
  { id: 'make',      Icon: LogoMake,   label: 'Make'      },
];

const bentoItems = [
  {
    title: 'High-Converting Websites', meta: 'Our Specialty',
    description: 'We design and build bespoke, ultra-fast websites focused on converting visitors into customers. From luxury aesthetics to advanced CMS integrations.',
    icon: <Globe className="w-4 h-4 text-blue-400" />, status: 'Core',
    tags: ['UI/UX', 'SEO', 'Performance'], colSpan: 2, hasPersistentHover: true, cta: 'Learn more →',
  },
  {
    title: 'Process Automation', meta: 'Save 40+ hrs/mo',
    description: 'Intelligent automated workflows using Make.com and n8n to connect your apps and run operations on autopilot.',
    icon: <Workflow className="w-4 h-4 text-emerald-400" />, status: 'Popular',
    tags: ['Make.com', 'n8n', 'CRM'], colSpan: 1, cta: 'Automate now →',
  },
  {
    title: 'AI Integration', meta: 'GPT / Claude',
    description: 'Custom AI chatbots, content pipelines, and intelligent agents built directly into your workflows.',
    icon: <Cpu className="w-4 h-4 text-purple-400" />, status: 'New',
    tags: ['Chatbots', 'Agents'], colSpan: 1, cta: 'Explore AI →',
  },
  {
    title: 'Custom Web Apps', meta: 'Full-Stack',
    description: 'Scalable, secure web applications tailored to your exact business logic or SaaS idea.',
    icon: <Code className="w-4 h-4 text-sky-400" />, status: 'Enterprise',
    tags: ['React', 'API', 'Database'], colSpan: 2, hasPersistentHover: true, cta: 'Start building →',
  },
];

const stats = [
  { stat: '200+',   label: 'Projects delivered'  },
  { stat: '98%',    label: 'Client satisfaction'  },
  { stat: '3.2x',   label: 'More conversions'     },
  { stat: '5 days', label: 'Average delivery'     },
];

const teamRows = [
  { icon: <Rocket    className="w-3.5 h-3.5 text-white/50" />, label: 'Startups & Founders',   note: '< 1 week kickoff'    },
  { icon: <Building2 className="w-3.5 h-3.5 text-white/50" />, label: 'In-House Dev Teams',    note: '100% async-friendly' },
  { icon: <Megaphone className="w-3.5 h-3.5 text-white/50" />, label: 'Marketing & Ops Teams', note: '3x faster delivery'  },
];

export default function Services() {
  const bentoVideoRef = useRef(null);
  const teamVideoRef  = useRef(null);

  // Bento section HLS video
  useEffect(() => {
    const video = bentoVideoRef.current;
    if (!video) return;
    const URL = 'https://stream.mux.com/r6pXRAJb3005XEEbl1hYU1x01RFJDSn7KQApwNGgAHHbU.m3u8';
    if (Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1, maxBufferLength: 10 });
      hls.loadSource(URL);
      hls.attachMedia(video);
      return () => hls.destroy();
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = URL;
    }
  }, []);

  // Team section HLS video
  useEffect(() => {
    const video = teamVideoRef.current;
    if (!video) return;
    const TEAM_URL = 'https://stream.mux.com/NcU3HlHeF7CUL86azTTzpy3Tlb00d6iF3BmCdFslMJYM.m3u8';
    if (Hls.isSupported()) {
      const hls = new Hls({ startLevel: -1, maxBufferLength: 10 });
      hls.loadSource(TEAM_URL);
      hls.attachMedia(video);
      return () => hls.destroy();
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = TEAM_URL;
    }
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-x-hidden flex flex-col">

      {/* Hero background video */}
      <div className="absolute top-0 left-0 w-full h-[70vh] z-0 overflow-hidden pointer-events-none">
        <video autoPlay loop muted playsInline
          className="w-full h-full object-cover opacity-35 mix-blend-screen">
          <source src={BackgroundVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/50 to-black pointer-events-none" />
      </div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[280px] bg-white/4 rounded-full blur-[100px] pointer-events-none z-0" />

      <div className="max-w-7xl w-full mx-auto px-4 sm:px-8 lg:px-16 relative z-10 flex-grow">

        {/* ── Header ────────────────────────────────────────────────── */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible"
          className="text-center mb-24 mt-8"
        >
          <div className="liquid-glass rounded-full px-4 py-1.5 inline-block mb-6">
            <span className="text-sm font-medium text-white font-body tracking-wide">Our Capabilities</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading italic tracking-tight leading-[0.9] mb-8">
            Everything you need to <br className="hidden md:block" /> scale seamlessly.
          </h1>
          <p className="text-white/60 font-body font-light text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            We don't just build software; we build growth engines. Explore our core services designed to modernise your operations and accelerate revenue.
          </p>
        </motion.div>

        {/* ── Logo Slider ────────────────────────────────────────────── */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
          className="relative w-full mb-24"
        >
          <div className="mx-auto w-full max-w-2xl">
            <div className="text-center text-2xl md:text-3xl text-white font-heading italic">
              <span className="text-white/50">Trusted by experts.</span>
              <br /><span>Powered by the best.</span>
            </div>
            <div className="relative mt-7 h-[70px] w-full overflow-hidden">
              <InfiniteSlider className="flex h-full w-full items-center" duration={35} gap={48}>
                {logos.map(({ id, Icon, label }) => (
                  <div key={id} className="flex flex-col items-center gap-1.5 w-20">
                    <Icon className="w-7 h-7 text-white/70" />
                    <span className="text-white/35 text-[10px] font-body tracking-wide">{label}</span>
                  </div>
                ))}
              </InfiniteSlider>
              {/* Gradient fade — left */}
              <div className="pointer-events-none absolute top-0 left-0 h-full w-[140px] bg-gradient-to-r from-black to-transparent z-10" />
              {/* Gradient fade — right */}
              <div className="pointer-events-none absolute top-0 right-0 h-full w-[140px] bg-gradient-to-l from-black to-transparent z-10" />
            </div>
          </div>
          <div className="relative -mt-20 h-56 w-full overflow-hidden [mask-image:radial-gradient(50%_50%,white,transparent)]">
            <div className="absolute inset-0 before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_bottom_center,#8350e8,transparent_70%)] before:opacity-35" />
            <div className="absolute -left-1/2 top-1/2 aspect-[1/0.7] z-10 w-[200%] rounded-[100%] border-t border-white/20 bg-black" />
            <Sparkles density={600}
              className="absolute inset-x-0 bottom-0 h-full w-full [mask-image:radial-gradient(50%_50%,white,transparent_85%)]"
              color="#ffffff" />
          </div>
        </motion.div>

        {/* ── Comprehensive Digital Solutions ───────────────────────── */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
          className="mb-28 relative z-10"
        >
          {/* HLS video background */}
          <div className="absolute -inset-y-16 -inset-x-4 sm:-inset-x-12 z-0 pointer-events-none overflow-hidden rounded-[3rem]">
            <video ref={bentoVideoRef} autoPlay loop muted playsInline
              className="absolute inset-0 w-full h-full object-cover opacity-35 mix-blend-screen" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,black_80%)] pointer-events-none" />
          </div>
          <div className="relative z-10">
            <div className="mb-10 text-center md:text-left pl-4 max-w-3xl">
              <h2 className="text-3xl md:text-4xl font-heading italic text-white mb-3">
                Comprehensive Digital Solutions
              </h2>
              <p className="text-white/60 font-body text-lg font-light leading-relaxed">
                We provide end-to-end services designed to elevate your brand, streamline your operations, and drive measurable growth.
              </p>
            </div>
            <BentoGrid items={bentoItems} />
          </div>
        </motion.div>

        {/* ── Team Collaboration ─────────────────────────────────────── */}
        <div className="mb-28 relative">
          {/* Full-width HLS team video */}
          <div className="absolute left-1/2 -translate-x-1/2 w-screen -top-32 bottom-0 z-0 pointer-events-none overflow-hidden">
            <video ref={teamVideoRef} autoPlay loop muted playsInline
              className="absolute inset-0 w-full h-full object-cover opacity-30 mix-blend-screen" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black pointer-events-none" />
          </div>

          <div className="relative z-10">
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={vp}
              className="text-center mb-10"
            >
              <div className="liquid-glass rounded-full px-4 py-1.5 inline-block mb-4">
                <span className="text-sm font-medium text-white font-body tracking-wide">How We Work</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-heading italic text-white mb-3">
                Built to fit your team, not the other way around.
              </h2>
              <p className="text-white/60 font-body font-light text-lg max-w-2xl mx-auto leading-relaxed">
                Whether you have a dedicated dev team, a marketing squad, or a solo founder — we slot in seamlessly and move at your pace.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
              <motion.div
                variants={fadeLeft} initial="hidden" whileInView="visible" viewport={vp}
                className="liquid-glass md:col-span-2 rounded-2xl p-6 flex flex-col justify-between gap-5"
              >
                <div>
                  <h3 className="text-white font-heading text-lg font-normal mb-1">Proven across every team type</h3>
                  <p className="text-white/45 font-body text-sm leading-relaxed">
                    From solo founders to enterprise squads — we integrate into your workflow and deliver measurable results.
                  </p>
                </div>
                <div className="grid grid-cols-4 gap-4 border-t border-white/10 pt-4">
                  {stats.map(item => (
                    <div key={item.label}>
                      <div className="text-2xl font-heading italic text-white mb-0.5">{item.stat}</div>
                      <div className="text-white/35 text-xs font-body">{item.label}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-1">
                  {teamRows.map(row => (
                    <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/5 border border-white/10">{row.icon}</span>
                        <span className="text-white/65 font-body text-sm">{row.label}</span>
                      </div>
                      <span className="text-white/30 font-body text-xs">{row.note}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                variants={fadeRightDelayed} initial="hidden" whileInView="visible" viewport={vp}
                className="liquid-glass-strong md:col-span-1 rounded-2xl"
              >
                <TeamSelector defaultValue={2} label="Your Team Size" />
              </motion.div>
            </div>
          </div>
        </div>

        {/* ── Next-Gen AI Infrastructure ─────────────────────────────── */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: false, margin: "-5% 0px -5% 0px" }}
          className="mb-28 relative z-10"
        >
          <div className="absolute -inset-y-20 -inset-x-4 sm:-inset-x-12 z-0 pointer-events-none overflow-hidden rounded-[4rem]">
            <video autoPlay loop muted playsInline
              className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-screen"
              style={{ objectPosition: 'center 60%' }}>
              <source src={ServiceBackgroundVideo} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,black_80%)] pointer-events-none" />
          </div>
          <AIBentoGrid />
        </motion.div>

        {/* ── Mini CTA ──────────────────────────────────────────────── */}
        <motion.div
          variants={scaleIn} initial="hidden" whileInView="visible" viewport={vp}
          className="liquid-glass rounded-[3rem] p-12 md:p-20 text-center border border-white/10 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
          <div className="relative z-10 flex flex-col items-center">
            <Zap className="w-9 h-9 text-white mb-5 opacity-75" />
            <h2 className="text-4xl md:text-5xl font-heading italic text-white tracking-tight leading-[0.9] mb-5">
              Not sure what you need?
            </h2>
            <p className="text-white/60 font-body font-light text-lg max-w-xl mx-auto leading-relaxed mb-8">
              Book a free discovery call. We'll analyse your current setup and identify exactly where automation or a new website will generate the highest ROI.
            </p>
            <LiquidMetalButton
              label="Book Discovery Call →"
              onClick={() => window.location.href = '/contact'}
            />
          </div>
        </motion.div>

      </div>
    </div>
  );
}
